import React from "react";
import styles from "./CoreFeature.module.scss";
import Image from "next/image";

function CoreFeature() {
  const coreFeatures = [
    {
      title: "Storefront Settings",
      description: "Comprehensive store configuration and management controls",
      icon: "/agent-icons/users.svg",
      image:
        "https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "User Access and Permissions management",
        "Taxation configuration and compliance",
        "Multi-channel Selling integration",
        "Business Settings and store configuration",
        "Logistics Settings and shipping rules",
        "3PL Controls and third-party logistics management",
      ],
      color: "#0d9488",
    },
    {
      title: "Advanced Product Catalog",
      description:
        "Sophisticated product management with AI-powered categorization",
      icon: "/agent-icons/message.svg",
      image:
        "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Dynamic product categorization and filtering",
        "Bulk product upload with CSV/Excel support",
        "AI-powered product recommendations",
        "Inventory management across multiple warehouses",
        "Product variant management (size, color, etc.)",
      ],
      color: "#f97316",
    },
    {
      title: "Order Management System",
      description: "End-to-end order processing with automated workflows",
      icon: "/agent-icons/cart.svg",
      image:
        "https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Real-time order tracking and status updates",
        "Automated order routing to vendors",
        "Return and refund management",
        "Split order handling for multiple vendors",
        "Order analytics and reporting dashboard",
      ],
      color: "#8b5cf6",
    },
    {
      title: "Payment Gateway Integration",
      description:
        "Secure, multi-gateway payment processing with fraud protection",
      icon: "/agent-icons/current.svg",
      image:
        "https://images.pexels.com/photos/4968391/pexels-photo-4968391.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Multiple payment gateway support (Stripe, PayPal, Razorpay)",
        "Split payment distribution to vendors",
        "Subscription and recurring payment handling",
        "Fraud detection and prevention",
        "PCI DSS compliance and security",
      ],
      color: "#22c55e",
    },
    {
      title: "Customer Management",
      description: "Comprehensive customer relationship and engagement tools",
      icon: "/agent-icons/target.svg",
      image:
        "https://images.pexels.com/photos/3184398/pexels-photo-3184398.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "360-degree customer profile management",
        "Segmentation and targeted marketing campaigns",
        "Loyalty program and rewards management",
        "Customer support ticket system",
        "Personalized shopping experiences",
      ],
      color: "#b36363",
    },
    {
      title: "Analytics & Reporting",
      description:
        "Business intelligence with real-time insights and forecasting",
      icon: "/agent-icons/bar.svg",
      image:
        "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Real-time sales and revenue dashboards",
        "Customer behavior and conversion analytics",
        "Vendor performance metrics",
        "Inventory turnover and demand forecasting",
        "Custom report builder with data export",
      ],
      color: "#0d9488",
    },
    {
      title: "Smart Inventory Controls",
      description:
        "Intelligent inventory management with automated optimization and forecasting",
      icon: "/agent-icons/bar.svg",
      image:
        "https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Real-time inventory tracking across multiple locations",
        "Automated reorder point calculations and alerts",
        "Demand forecasting with AI-powered predictions",
        "Low stock notifications and emergency procurement",
        "Inventory turnover analysis and optimization recommendations",
        "Batch and expiry date management for perishable goods",
      ],
      color: "#cf7494",
    },
    {
      title: "Offers and Promotions",
      description:
        "Comprehensive promotional tools to drive sales and customer engagement",
      icon: "/agent-icons/star.svg",
      image:
        "https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Dynamic discount and coupon management",
        "Flash sales and time-limited offers",
        "Buy-one-get-one (BOGO) and bundle promotions",
        "Loyalty program and rewards management",
        "Automated promotional campaigns and scheduling",
        "Cross-selling and upselling promotion tools",
      ],
      color: "#1bbbe3",
    },
    {
      title: "Content Management System",
      description:
        "Comprehensive content management for web and mobile platforms",
      icon: "/agent-icons/globe.svg",
      image:
        "https://images.pexels.com/photos/270637/pexels-photo-270637.jpeg?auto=compress&cs=tinysrgb&w=800",
      functionalities: [
        "Dynamic page builder with drag-and-drop interface",
        "Mobile-responsive content templates and themes",
        "SEO optimization tools and meta management",
        "Multi-language content management and localization",
        "Media library with image and video optimization",
        "Blog and article management system",
      ],
      color: "#eab308",
    },
  ];

  return (
    <section className={styles["core-section"]}>
      <div className={styles["core-container"]}>
        <div className={styles["core-header"]}>
          <h2 className="heading">Core Platform Functionalities</h2>
          <p className="subHeading">
            Comprehensive feature set designed to handle every aspect of modern
            e-commerce operations
          </p>
        </div>

        <div className={styles["core-grid"]}>
          {coreFeatures?.map((feature, index) => (
            <div key={index} className={styles.card}>
              <div className={styles.imageContainer}>
                <img
                  src={feature?.image}
                  alt={feature?.title}
                  className={styles.image}
                />
                <div
                  className={styles.overlay}
                  style={{
                    background: `linear-gradient(135deg, ${feature?.color}22, ${feature?.color}44)`,
                  }}
                />
              </div>
              <div className={styles.content}>
                <div className={styles.header}>
                  <h3 className={styles.title}>{feature?.title}</h3>
                </div>
                <p className={styles.description}>{feature?.description}</p>
                <ul className={styles.functionalities}>
                  {feature?.functionalities.map((item, idx) => (
                    <li key={idx} className={styles.functionalityItem}>
                      <div
                        className={styles.checkIcon}
                        style={{
                          backgroundColor: `${feature?.color}15`,
                          color: feature?.color,
                        }}
                      >
                        <Image
                          src="/agent-icons/current.svg"
                          alt="current"
                          width={10}
                          height={10}
                          className={styles["feature-icon"]}
                        />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CoreFeature;
